import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { awardXP } from '../utils/xp';
import { speakText, stopSpeaking } from '../utils/speak';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { loadVocabularyPool, shuffle } from '../data/vocabPool';
import './Vocabulary.css';

const FLASHCARD_SESSION = 15;
const QUIZ_SESSION = 10;

const TEXT = {
    en: {
        title: 'Vocabulary',
        loading: 'Loading your words...',
        ready: '{count} words ready to practice',
        fromLessons: 'Words come from your lessons and your word bank. Finish more lessons to add more.',
        flashcards: 'Flashcards',
        flashcardsDesc: 'Flip each card and say whether you knew it',
        quiz: 'Quiz',
        quizDesc: '10 multiple choice questions',
        quizNeedsMore: 'The quiz needs at least 4 words with meanings.',
        tapToFlip: 'Tap the card to see the meaning',
        noMeaning: 'No translation for this one. Say it out loud and listen to the sound.',
        again: 'Again',
        gotIt: 'Got it',
        listen: 'Listen',
        card: 'Card {current} of {total}',
        question: 'Question {current} of {total}',
        meaning: 'What does “{word}” mean?',
        reverse: 'Which word means “{translation}”?',
        next: 'Next →',
        seeResults: 'See results →',
        done: 'Session complete',
        flashResult: 'You knew {known} of {total} cards.',
        quizResult: 'You got {score} of {total} right.',
        xp: '+{xp} XP',
        review: 'Words to review',
        practiceAgain: 'Practice again',
        back: 'Back to home',
        empty: 'There are no words to practice yet. Finish a lesson first.',
    },
    it: {
        title: 'Vocabolario',
        loading: 'Caricamento delle tue parole...',
        ready: '{count} parole pronte da esercitare',
        fromLessons: 'Le parole vengono dalle tue lezioni e dal tuo word bank. Completa altre lezioni per aggiungerne.',
        flashcards: 'Flashcard',
        flashcardsDesc: 'Gira ogni carta e dì se la sapevi',
        quiz: 'Quiz',
        quizDesc: '10 domande a scelta multipla',
        quizNeedsMore: 'Il quiz richiede almeno 4 parole con un significato.',
        tapToFlip: 'Tocca la carta per vedere il significato',
        noMeaning: 'Per questa non c’è una traduzione. Dilla ad alta voce e ascolta il suono.',
        again: 'Ancora',
        gotIt: 'Lo so',
        listen: 'Ascolta',
        card: 'Carta {current} di {total}',
        question: 'Domanda {current} di {total}',
        meaning: 'Che cosa significa “{word}”?',
        reverse: 'Quale parola significa “{translation}”?',
        next: 'Avanti →',
        seeResults: 'Vedi i risultati →',
        done: 'Sessione completata',
        flashResult: 'Conoscevi {known} carte su {total}.',
        quizResult: 'Hai risposto bene a {score} domande su {total}.',
        xp: '+{xp} XP',
        review: 'Parole da ripassare',
        practiceAgain: 'Esercitati ancora',
        back: 'Torna alla home',
        empty: 'Non ci sono ancora parole da esercitare. Completa prima una lezione.',
    },
    es: {
        title: 'Vocabulario',
        loading: 'Cargando tus palabras...',
        ready: '{count} palabras listas para practicar',
        fromLessons: 'Las palabras vienen de tus lecciones y de tu banco de palabras. Completa más lecciones para añadir más.',
        flashcards: 'Tarjetas',
        flashcardsDesc: 'Da la vuelta a cada tarjeta y di si la sabías',
        quiz: 'Cuestionario',
        quizDesc: '10 preguntas de opción múltiple',
        quizNeedsMore: 'El cuestionario necesita al menos 4 palabras con significado.',
        tapToFlip: 'Toca la tarjeta para ver el significado',
        noMeaning: 'Esta no tiene traducción. Dila en voz alta y escucha el sonido.',
        again: 'Otra vez',
        gotIt: 'Lo sé',
        listen: 'Escuchar',
        card: 'Tarjeta {current} de {total}',
        question: 'Pregunta {current} de {total}',
        meaning: '¿Qué significa “{word}”?',
        reverse: '¿Qué palabra significa “{translation}”?',
        next: 'Siguiente →',
        seeResults: 'Ver resultados →',
        done: 'Sesión completada',
        flashResult: 'Sabías {known} de {total} tarjetas.',
        quizResult: 'Acertaste {score} de {total}.',
        xp: '+{xp} XP',
        review: 'Palabras para repasar',
        practiceAgain: 'Practicar otra vez',
        back: 'Volver al inicio',
        empty: 'Todavía no hay palabras para practicar. Completa primero una lección.',
    },
    fr: {
        title: 'Vocabulaire',
        loading: 'Chargement de tes mots...',
        ready: '{count} mots prêts à pratiquer',
        fromLessons: 'Les mots viennent de tes leçons et de ton carnet de mots. Termine d’autres leçons pour en ajouter.',
        flashcards: 'Cartes mémoire',
        flashcardsDesc: 'Retourne chaque carte et dis si tu la connaissais',
        quiz: 'Quiz',
        quizDesc: '10 questions à choix multiples',
        quizNeedsMore: 'Le quiz demande au moins 4 mots avec un sens.',
        tapToFlip: 'Touche la carte pour voir le sens',
        noMeaning: 'Pas de traduction pour celui-ci. Dis-le à voix haute et écoute le son.',
        again: 'Encore',
        gotIt: 'Je sais',
        listen: 'Écouter',
        card: 'Carte {current} sur {total}',
        question: 'Question {current} sur {total}',
        meaning: 'Que signifie « {word} » ?',
        reverse: 'Quel mot signifie « {translation} » ?',
        next: 'Suivant →',
        seeResults: 'Voir les résultats →',
        done: 'Séance terminée',
        flashResult: 'Tu connaissais {known} cartes sur {total}.',
        quizResult: 'Tu as bien répondu à {score} questions sur {total}.',
        xp: '+{xp} XP',
        review: 'Mots à revoir',
        practiceAgain: 'Pratiquer encore',
        back: 'Retour à l’accueil',
        empty: 'Il n’y a pas encore de mots à pratiquer. Termine d’abord une leçon.',
    },
    pt: {
        title: 'Vocabulário',
        loading: 'Carregando suas palavras...',
        ready: '{count} palavras prontas para praticar',
        fromLessons: 'As palavras vêm das suas lições e do seu banco de palavras. Conclua mais lições para adicionar mais.',
        flashcards: 'Cartões',
        flashcardsDesc: 'Vire cada cartão e diga se você sabia',
        quiz: 'Quiz',
        quizDesc: '10 perguntas de múltipla escolha',
        quizNeedsMore: 'O quiz precisa de pelo menos 4 palavras com significado.',
        tapToFlip: 'Toque no cartão para ver o significado',
        noMeaning: 'Esta não tem tradução. Diga em voz alta e ouça o som.',
        again: 'De novo',
        gotIt: 'Sei',
        listen: 'Ouvir',
        card: 'Cartão {current} de {total}',
        question: 'Pergunta {current} de {total}',
        meaning: 'O que significa “{word}”?',
        reverse: 'Qual palavra significa “{translation}”?',
        next: 'Próxima →',
        seeResults: 'Ver resultados →',
        done: 'Sessão concluída',
        flashResult: 'Você sabia {known} de {total} cartões.',
        quizResult: 'Você acertou {score} de {total}.',
        xp: '+{xp} XP',
        review: 'Palavras para revisar',
        practiceAgain: 'Praticar de novo',
        back: 'Voltar ao início',
        empty: 'Ainda não há palavras para praticar. Conclua primeiro uma lição.',
    },
};

// Builds quiz questions. Each question asks for the meaning of a word, or for the word that has a given meaning.
function buildQuiz(pool, count) {
    const withMeaning = pool.filter(item => item.translation);
    const chosen = shuffle(withMeaning).slice(0, count);
    return chosen.map(item => {
        const reverse = Math.random() < 0.4;
        const distractors = shuffle(withMeaning.filter(other => other.word !== item.word));
        const options = [];
        const seen = new Set([reverse ? item.word : item.translation]);
        for (const other of distractors) {
            const text = reverse ? other.word : other.translation;
            if (seen.has(text)) continue;
            seen.add(text);
            options.push(text);
            if (options.length === 3) break;
        }
        const correctText = reverse ? item.word : item.translation;
        const allOptions = shuffle([correctText, ...options]);
        return {
            item,
            reverse,
            options: allOptions,
            correctIndex: allOptions.indexOf(correctText),
        };
    });
}

export default function Vocabulary() {
    const navigate = useNavigate();
    const { interfaceLanguage } = useLanguage();
    const { activeLanguage } = useUserLanguage();

    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const uiLang = TEXT[interfaceLanguage] ? interfaceLanguage : 'en';
    const s = (key, values) => {
        let text = TEXT[uiLang][key] || TEXT.en[key];
        Object.keys(values || {}).forEach(name => {
            text = text.replace(`{${name}}`, values[name]);
        });
        return text;
    };

    const [loading, setLoading] = useState(true);
    const [pool, setPool] = useState([]);
    const [mode, setMode] = useState(null);

    // Flashcards
    const [deck, setDeck] = useState([]);
    const [position, setPosition] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [known, setKnown] = useState([]);
    const [missed, setMissed] = useState([]);
    const [requeued, setRequeued] = useState({});
    const [flashTotal, setFlashTotal] = useState(0);

    // Quiz
    const [questions, setQuestions] = useState([]);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [picked, setPicked] = useState(null);
    const [score, setScore] = useState(0);
    const [missedQuiz, setMissedQuiz] = useState([]);

    const [finished, setFinished] = useState(false);
    const [earnedXp, setEarnedXp] = useState(0);
    const xpGiven = useRef(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) { navigate('/signin'); return; }
            const items = await loadVocabularyPool(user.id, targetLang, uiLang);
            if (cancelled) return;
            setPool(items);
            setLoading(false);
        };
        load();
        return () => { cancelled = true; };
    }, [targetLang, uiLang, navigate]);

    useEffect(() => () => stopSpeaking(), []);

    const quizPossible = pool.filter(item => item.translation).length >= 4;

    const startFlashcards = () => {
        const cards = shuffle(pool).slice(0, FLASHCARD_SESSION);
        setDeck(cards);
        setFlashTotal(cards.length);
        setPosition(0);
        setFlipped(false);
        setKnown([]);
        setMissed([]);
        setRequeued({});
        setFinished(false);
        setEarnedXp(0);
        xpGiven.current = false;
        setMode('flashcards');
    };

    const startQuiz = () => {
        const built = buildQuiz(pool, QUIZ_SESSION);
        setQuestions(built);
        setQuestionIndex(0);
        setPicked(null);
        setScore(0);
        setMissedQuiz([]);
        setFinished(false);
        setEarnedXp(0);
        xpGiven.current = false;
        setMode('quiz');
    };

    const giveXp = (xp) => {
        if (xpGiven.current) return;
        xpGiven.current = true;
        setEarnedXp(xp);
        if (xp > 0) awardXP(xp, 'vocabulary');
    };

    const speak = (word) => {
        stopSpeaking();
        speakText(word, undefined, targetLang);
    };

    const answerCard = (knewIt) => {
        const card = deck[position];
        let nextDeck = deck;
        let nextKnown = known;
        let nextMissed = missed;
        let nextRequeued = requeued;
        if (knewIt) {
            nextKnown = [...known, card];
            setKnown(nextKnown);
        } else {
            if (!missed.find(m => m.word === card.word)) {
                nextMissed = [...missed, card];
                setMissed(nextMissed);
            }
            // A missed card comes back once more at the end
            if (!requeued[card.word]) {
                nextRequeued = { ...requeued, [card.word]: true };
                setRequeued(nextRequeued);
                nextDeck = [...deck, card];
                setDeck(nextDeck);
            }
        }
        setFlipped(false);
        if (position + 1 >= nextDeck.length) {
            setFinished(true);
            giveXp(Math.min(20, nextKnown.length * 2));
        } else {
            setPosition(position + 1);
        }
    };

    const pickAnswer = (index) => {
        if (picked !== null) return;
        setPicked(index);
        const question = questions[questionIndex];
        if (index === question.correctIndex) {
            setScore(score + 1);
        } else {
            setMissedQuiz([...missedQuiz, question.item]);
        }
    };

    const nextQuestion = () => {
        if (questionIndex + 1 >= questions.length) {
            setFinished(true);
            giveXp(score * 2);
        } else {
            setQuestionIndex(questionIndex + 1);
            setPicked(null);
        }
    };

    const backToMenu = () => {
        stopSpeaking();
        setMode(null);
        setFinished(false);
    };

    if (loading) {
        return (
            <div className="vb-screen">
                <div className="vb-top-bar">
                    <button className="vb-close" onClick={() => navigate('/home')}>✕</button>
                    <div className="vb-title">{s('title')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="vb-state">{s('loading')}</div>
            </div>
        );
    }

    // ---------- Menu ----------
    if (!mode) {
        return (
            <div className="vb-screen">
                <div className="vb-top-bar">
                    <button className="vb-close" onClick={() => navigate('/home')}>✕</button>
                    <div className="vb-title">{s('title')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="vb-menu">
                    <div className="vb-menu-icon">📖</div>
                    {pool.length === 0 ? (
                        <div className="vb-state">{s('empty')}</div>
                    ) : (
                        <>
                            <div className="vb-menu-title">{s('ready', { count: pool.length })}</div>
                            <div className="vb-menu-sub">{s('fromLessons')}</div>

                            <button className="vb-mode-card" onClick={startFlashcards}>
                                <div className="vb-mode-emoji">🗂️</div>
                                <div>
                                    <div className="vb-mode-name">{s('flashcards')}</div>
                                    <div className="vb-mode-desc">{s('flashcardsDesc')}</div>
                                </div>
                            </button>

                            <button className="vb-mode-card" onClick={startQuiz} disabled={!quizPossible}>
                                <div className="vb-mode-emoji">❓</div>
                                <div>
                                    <div className="vb-mode-name">{s('quiz')}</div>
                                    <div className="vb-mode-desc">{quizPossible ? s('quizDesc') : s('quizNeedsMore')}</div>
                                </div>
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ---------- Results ----------
    if (finished) {
        const reviewList = mode === 'flashcards' ? missed : missedQuiz;
        return (
            <div className="vb-screen">
                <div className="vb-top-bar">
                    <button className="vb-close" onClick={backToMenu}>✕</button>
                    <div className="vb-title">{s('title')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="vb-results">
                    <div className="vb-results-icon">🎉</div>
                    <div className="vb-results-title">{s('done')}</div>
                    <div className="vb-results-score">
                        {mode === 'flashcards'
                            ? s('flashResult', { known: known.length, total: flashTotal })
                            : s('quizResult', { score, total: questions.length })}
                    </div>
                    {earnedXp > 0 && <div className="vb-results-xp">{s('xp', { xp: earnedXp })}</div>}

                    {reviewList.length > 0 && (
                        <div className="vb-review">
                            <div className="vb-review-label">{s('review')}</div>
                            {reviewList.map(item => (
                                <div key={item.word} className="vb-review-row">
                                    <div>
                                        <div className="vb-review-word">{item.word}</div>
                                        {item.translation && <div className="vb-review-meaning">{item.translation}</div>}
                                    </div>
                                    <button className="vb-listen-btn" onClick={() => speak(item.word)} aria-label={s('listen')}>🔊</button>
                                </div>
                            ))}
                        </div>
                    )}

                    <button className="vb-primary-btn" onClick={mode === 'flashcards' ? startFlashcards : startQuiz}>
                        {s('practiceAgain')}
                    </button>
                    <button className="vb-secondary-btn" onClick={() => navigate('/home')}>{s('back')}</button>
                </div>
            </div>
        );
    }

    // ---------- Flashcards ----------
    if (mode === 'flashcards') {
        const card = deck[position];
        return (
            <div className="vb-screen">
                <div className="vb-top-bar">
                    <button className="vb-close" onClick={backToMenu}>✕</button>
                    <div className="vb-title">{s('flashcards')}</div>
                    <div className="vb-counter">{s('card', { current: Math.min(position + 1, deck.length), total: deck.length })}</div>
                </div>
                <div className="vb-content">
                    <div className="vb-card" onClick={() => setFlipped(!flipped)}>
                        {card.emoji && <div className="vb-card-emoji">{card.emoji}</div>}
                        <div className="vb-card-word">{card.word}</div>
                        {card.phonetic && <div className="vb-card-phonetic">{card.phonetic}</div>}
                        {!flipped ? (
                            <div className="vb-card-hint">{s('tapToFlip')}</div>
                        ) : (
                            <div className="vb-card-back">
                                {card.translation
                                    ? <div className="vb-card-meaning">{card.translation}</div>
                                    : <div className="vb-card-hint">{s('noMeaning')}</div>}
                                {card.note && <div className="vb-card-note">{card.note}</div>}
                            </div>
                        )}
                    </div>

                    <button className="vb-listen-wide" onClick={() => speak(card.word)}>🔊 {s('listen')}</button>

                    {flipped && (
                        <div className="vb-answer-row">
                            <button className="vb-again-btn" onClick={() => answerCard(false)}>{s('again')}</button>
                            <button className="vb-got-btn" onClick={() => answerCard(true)}>{s('gotIt')}</button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ---------- Quiz ----------
    const question = questions[questionIndex];
    const prompt = question.reverse
        ? s('reverse', { translation: question.item.translation })
        : s('meaning', { word: question.item.word });

    return (
        <div className="vb-screen">
            <div className="vb-top-bar">
                <button className="vb-close" onClick={backToMenu}>✕</button>
                <div className="vb-title">{s('quiz')}</div>
                <div className="vb-counter">{s('question', { current: questionIndex + 1, total: questions.length })}</div>
            </div>
            <div className="vb-content">
                <div className="vb-question-card">
                    <div className="vb-question-text">{prompt}</div>
                    {!question.reverse && (
                        <button className="vb-listen-btn" onClick={() => speak(question.item.word)} aria-label={s('listen')}>🔊</button>
                    )}
                </div>

                <div className="vb-options">
                    {question.options.map((option, index) => {
                        let stateClass = '';
                        if (picked !== null) {
                            if (index === question.correctIndex) stateClass = 'correct';
                            else if (index === picked) stateClass = 'wrong';
                        }
                        return (
                            <button
                                key={index}
                                className={`vb-option ${stateClass}`}
                                onClick={() => pickAnswer(index)}
                                disabled={picked !== null}
                            >
                                {option}
                            </button>
                        );
                    })}
                </div>

                {picked !== null && (
                    <button className="vb-primary-btn" onClick={nextQuestion}>
                        {questionIndex + 1 >= questions.length ? s('seeResults') : s('next')}
                    </button>
                )}
            </div>
        </div>
    );
}